import React, { Fragment as F, Suspense } from 'react'
import { connect } from 'react-redux'
import c from 'classnames'
import isEmpty from 'lodash/isEmpty'
import debounce from 'lodash/debounce'
import Tabs from 'discada/dist/Tabs'

import Header from 'components/VRP/Header'
import Gallery from 'components/VRP/Gallery'
import Info from 'components/VRP/VehicleInfo'
import Equipment from 'components/VRP/Equipment'
import Warranties from 'components/VRP/Warranty'
import DetailedSpecs from 'components/VRP/DetailedSpecs'
import LoadingQuote from 'components/AppStatus/LoadingQuote'
import Quote from 'components/NewQuote'

import { getEntity } from 'selectors/entitySelectors'
import { initInventoryDetails } from 'actions/inventoryActions'

import { createQuote } from 'actions/quoteActions'

import { isUserWet } from 'selectors/naughtySelectors'
import { setPageTitle, addAndReplacePageState } from 'actions/pageStateActions'
import { getActiveHash, getQueryValue } from 'selectors/pageStateSelectors'
import {
  getIsUsedCar,
  hasWarrantyData,
  hasEquipment,
  hasSpecs,
} from 'selectors/inventorySelectors'

import {
  getQuote,
  getQuoteDirty,
  getQuoteConfig,
  getQuoteValue,
  quoteHasPrograms,
  getIsLoading,
  doesQuoteExist,
  getIsGettingPrograms,
} from 'selectors/quoteSelectors'

import { getWritePermission } from 'selectors/authSelectors'
import PricingCardFloater from 'components/VRP/PricingCardFloater'
import {
  isFramedIn,
  isMobile,
  getTaggedCustomerFromState,
} from 'selectors/appStatusSelectors'
import NoPrograms from 'components/NewQuote/NoPrograms'

import './index.scss'

const NewHistory = React.lazy(() => import('components/VRP/NewHistory'))
const DealWorkArea = React.lazy(() => import('components/Quote/DealWorkArea'))

export const VRP_SECTIONS = {
  'vrp-gallery': 'Photo Gallery',
  'vrp-pricing': 'Pricing & Payments',
  'vrp-warranties': 'Warranties',
  'vrp-safety': 'Safety Ratings',
  'vrp-equipment': 'Standard Equipment',
}

export class VRP extends React.Component {
  constructor(...args) {
    super(...args)

    this.state = { scrollTop: 0 }

    this.setScrollTop = debounce(
      () => this.setState({ scrollTop: window.scrollY }),
      1,
    )
  }

  componentDidMount() {
    let { vehicleId, quoteId, initInventoryDetails } = this.props

    initInventoryDetails(vehicleId, quoteId)
    window.addEventListener('scroll', this.setScrollTop)
  }

  componentDidUpdate(prevProps) {
    let { id, quoteId, createQuote, urlZip, quoteZip } = this.props,
      addendum = {},
      isQuoteIdDiff =
        prevProps.quoteId && quoteId && prevProps.quoteId !== quoteId,
      isZipDiff =
        prevProps.quoteZip !== quoteZip &&
        !isEmpty(quoteZip) &&
        !isEmpty(urlZip) &&
        quoteZip !== urlZip

    if (isZipDiff) addendum.customerZipCode = urlZip

    if (isZipDiff || isQuoteIdDiff)
      createQuote(id, quoteId, addendum, true, true)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.setScrollTop)
  }

  render() {
    let {
        id,
        isLoading,
        className,
        showHeader,
        scrollContainer,
        vehicle,
        quoteId,
        dirty,
        isManager,
        showCustomerView,
        activeHash,
        mode,
        showWarranties,
        showEquipment,
        showSpecs,
        quoteHasPrograms,
        isQuoteLoading,
      } = this.props,
      { scrollTop } = this.state,
      shouldShowCustomerView = !isManager || showCustomerView,
      showFloater = scrollTop > 100,
      detailsTabs = [
        {
          tabKey: 'info',
          label: 'About',
          content: <Info id={id} />,
        },
        showWarranties
          ? {
              tabKey: 'warranty',
              label: 'Warranties',
              content: <Warranties id={id} quoteId={quoteId} mode={mode} />,
            }
          : null,
        showEquipment
          ? {
              tabKey: 'equipment',
              label: 'Equipment',
              content: <Equipment id={id} />,
            }
          : null,
        showSpecs
          ? {
              tabKey: 'spec',
              label: 'Specifications',
              content: <DetailedSpecs id={id} />,
            }
          : null,
      ].filter(x => x)

    return (
      <div
        className={c('vrp', className, {
          rich: vehicle.rich,
          dirty,
        })}
      >
        {isLoading ? (
          <LoadingQuote showProgressBar />
        ) : (
          <div className="vrp-inner">
            <PricingCardFloater
              {...this.props}
              vehicleId={id}
              mode={activeHash}
              className={c({ show: showFloater })}
            />
            <div id="vrp-pricing">
              <div
                className={c('gallery-wrapper', { 'has-header': showHeader })}
              >
                <Gallery
                  id={id}
                  noswitch
                  shouldShowCustomerView={shouldShowCustomerView}
                />
              </div>
              {showHeader && (
                <Header
                  id={id}
                  className={c('full-page', {
                    'show-pricing-cards': showFloater,
                  })}
                  scrollContainer={scrollContainer}
                  showPricingCards={showFloater}
                  showScrollButtons
                />
              )}
              {!shouldShowCustomerView ? (
                <Suspense fallback={<span />}>
                  <NewHistory
                    vehicleId={id}
                    quoteId={quoteId}
                    startingQuoteId={vehicle.quoteId}
                  />
                  {(quoteHasPrograms || isQuoteLoading) && (
                    <DealWorkArea
                      vehicleId={id}
                      quoteId={quoteId}
                      mode={activeHash}
                    />
                  )}
                </Suspense>
              ) : (
                <F>
                  {(quoteHasPrograms || isQuoteLoading) && (
                    <Quote vehicleId={id} quoteId={quoteId} mode={activeHash} />
                  )}
                </F>
              )}
              {quoteId &&
                !isLoading &&
                !isQuoteLoading &&
                !quoteHasPrograms && <NoPrograms />}
            </div>

            <div className="details-tabs">
              <Tabs tabs={detailsTabs} centered />
            </div>
          </div>
        )}
      </div>
    )
  }
}

function mapStateToProps(state, op) {
  let quoteId = op.quoteId,
    vehicleId = op.vehicleId || op.id,
    vehicle = getEntity(state, vehicleId),
    quote = getQuote(state, op.vehicleId, op.quoteId),
    activeHash = getActiveHash(state),
    newActiveHash = activeHash,
    taggedCustomer = getTaggedCustomerFromState(state)

  if (quote.quotedPayment && quote.mode) newActiveHash = quote.mode

  return {
    ...op,
    vehicle,
    vehicleId,
    quoteId,
    mode: quote.mode,
    isUsed: getIsUsedCar(state, op.id),
    activeHash: newActiveHash,
    isLoading: isEmpty(vehicle) || !vehicle.rich,
    isQuoteLoading:
      getIsLoading(state, op.vehicleId, op.quoteId) ||
      getIsGettingPrograms(state, op.vehicleId, op.quoteId),
    dirty: getQuoteDirty(state, vehicleId, quoteId),
    configExists: !isEmpty(getQuoteConfig(state)),
    isManager: getWritePermission(state, 'deal.fields/profit'),
    showCustomerView: !!getQueryValue(state, 'customerView') || isFramedIn(),
    isMobile: isMobile(state),
    quoteZip: getQuoteValue(state, op.vehicleId, op.quoteId, 'customerZipCode'),
    quoteHasPrograms: !!quoteHasPrograms(state, op.vehicleId, op.quoteId),
    urlZip: getQueryValue(state, 'zipcode'),
    showWarranties: hasWarrantyData(state, op.id),
    showEquipment: hasEquipment(state, op.id),
    showSpecs: hasSpecs(state, op.id),
    quoteExists: doesQuoteExist(state, quoteId),
    taggedCustomer,
    userIsWet: isUserWet(state),
  }
}

export { Header, Gallery }

export default connect(
  mapStateToProps,
  {
    initInventoryDetails,
    createQuote,
    setPageTitle,
    addAndReplacePageState,
  },
)(VRP)
